import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';

import { operatorTypeNameFromOperatorType } from '../../../../../components/filter-editor/constants/intl-messages/operator-type-messages';
import { useEnableOrderBrandFilter } from '../../../../../feature-flags/hooks/use-enable-order-brand-filter';
import { useEnableFulfillmentProviderFilter } from '../../../../../hooks/use-enable-fulfillment-provider-filter';
import { useEnableShopifyShipFromFilter } from '../../../../../hooks/use-enable-shopify-ship-from-filter';
import type { WorkflowStep, WorkflowFilterDraft, WorkflowEntityType } from '../../types';
import { getFilterRegistry } from '../../registries';

import type { FilterRegistry } from '../../registries/registry-types';
import { messages } from './messages';
import styles from './styles.module.less';

const emptyValueForField = (fieldName: string, registry: FilterRegistry): string | string[] =>
  registry[fieldName]?.filterKind === 'collection' ? [] : '';

type FilterSectionProps = {
  step: WorkflowStep;
  entityType: WorkflowEntityType;
  onUpdateStep: (step: WorkflowStep) => void;
};

export const FilterSection: React.FC<FilterSectionProps> = ({ step, entityType, onUpdateStep }) => {
  const intl = useIntl();
  const hasOrderBrandFilterFlag = useEnableOrderBrandFilter();
  const hasFulfillmentProviderFlag = useEnableFulfillmentProviderFilter();
  const hasShopifyStore = useEnableShopifyShipFromFilter();
  const filters = useMemo(() => step.filters ?? [], [step.filters]);
  const filterRegistry = useMemo(() => getFilterRegistry(entityType), [entityType]);

  const fieldOptions = useMemo(
    () =>
      Object.entries(filterRegistry).reduce<Array<{ value: string; label: string }>>((acc, [fieldName, entry]) => {
        if (fieldName === 'SellerBrandId' && !hasOrderBrandFilterFlag) {
          return acc;
        }
        if (fieldName === 'SellerFillProviderId' && !hasFulfillmentProviderFlag) {
          return acc;
        }
        if (fieldName === 'ShopifyShipFromLocation' && !hasShopifyStore) {
          return acc;
        }

        acc.push({ value: fieldName, label: entry.labelMessage ? intl.formatMessage(entry.labelMessage) : '' });
        return acc;
      }, []),
    [filterRegistry, intl, hasOrderBrandFilterFlag, hasFulfillmentProviderFlag, hasShopifyStore],
  );

  const handleAddFilter = useCallback(() => {
    const newFilter: WorkflowFilterDraft = {
      id: GuidService.newGuid(),
      fieldName: '',
      operator: '',
      value: '',
    };
    onUpdateStep({ ...step, filters: [...filters, newFilter] });
  }, [step, filters, onUpdateStep]);

  const handleUpdateFilter = useCallback(
    (filterId: string, updates: Partial<WorkflowFilterDraft>) => {
      const updatedFilters = filters.map((filter) => (filter.id === filterId ? { ...filter, ...updates } : filter));
      onUpdateStep({ ...step, filters: updatedFilters });
    },
    [step, filters, onUpdateStep],
  );

  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const updatedFilters = filters.filter((filter) => filter.id !== filterId);
      onUpdateStep({ ...step, filters: updatedFilters });
    },
    [step, filters, onUpdateStep],
  );

  return (
    <FlexColumn gap={Gap.Space12}>
      {filters.map((filter) => {
        const registryEntry = filterRegistry[filter.fieldName];
        const operatorOptions = registryEntry
          ? registryEntry.operators.map((op) => ({
              value: op,
              label: intl.formatMessage(operatorTypeNameFromOperatorType(op) ?? { defaultMessage: op, description: '' }),
            }))
          : [];
        const ValueInput = registryEntry?.valueInputsByOperator[filter.operator] ?? null;

        return (
          <FlexRow key={filter.id} className={styles['filter-row']} gap={Gap.Space16}>
            <SearchableDropdownField
              className={styles['filter-select']}
              id={`${filter.id}-field`}
              label=''
              onItemSelected={(value) =>
                handleUpdateFilter(filter.id, { fieldName: value, operator: '', value: emptyValueForField(value, filterRegistry) })
              }
              options={fieldOptions}
              placeholder={intl.formatMessage(messages.selectFilterType)}
              selectedItemValue={filter.fieldName}
            />
            <SearchableDropdownField
              className={styles['operator-select']}
              id={`${filter.id}-operator`}
              label=''
              onItemSelected={(value) =>
                handleUpdateFilter(filter.id, { operator: value, value: emptyValueForField(filter.fieldName, filterRegistry) })
              }
              options={operatorOptions}
              placeholder={intl.formatMessage(messages.selectOperator)}
              selectedItemValue={filter.operator}
            />
            {ValueInput && (
              <ValueInput
                className={styles['value-input']}
                onChange={(v: string | string[]) => handleUpdateFilter(filter.id, { value: v })}
                value={filter.value}
              />
            )}
            <Button
              ariaLabel={intl.formatMessage(messages.removeFilterAriaLabel)}
              className={styles['remove-button']}
              onClick={() => handleRemoveFilter(filter.id)}
              theme={THEME_CONSTANTS.unstyled}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </FlexRow>
        );
      })}
      <Button onClick={handleAddFilter} theme={THEME_CONSTANTS.tertiary}>
        {intl.formatMessage(messages.addFilterButton)}
      </Button>
    </FlexColumn>
  );
};
